import { Box } from '@mui/material';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import type { UserRole } from '@/types';

import { getUserRoleIds, isAuthenticated } from '@/data/service/authService';
import type { RouteType } from '@/routes';
import { routes } from '@/routes';

import { ScrollProvider } from '@/context/scrollContext';
import AuthenticatedSnowplow from '../providers/authenticated-snowplow';
import { AmazonConnect } from './AmazonConnect/AmazonConnect';
import { AmazonConnectProvider } from './AmazonConnect/AmazonConnectProvider';
import { SideBar } from './navigation/SideBar';

type AppRouteType = {
  isPrivate: boolean;
  restrictAccess: UserRole[];
};

type AppRoutesProps = {
  appRoutes?: RouteType[];
};

const AppRoute = ({ isPrivate, restrictAccess }: AppRouteType) => {
  const isAuthorized = isAuthenticated();

  const userRoleIds = getUserRoleIds();
  const shouldRestrictAccess =
    isAuthorized && Array.isArray(restrictAccess) ? userRoleIds.some((id) => restrictAccess.includes(id)) : false;

  if (shouldRestrictAccess) {
    return <Navigate to="/" />;
  }

  return isPrivate && !isAuthorized ? <Navigate to="/" /> : <Outlet />;
};

export const AppRoutesWithoutRouter = ({ appRoutes = routes }: AppRoutesProps) => (
  <Routes>
    {appRoutes.map(({ path, component: Page, navBar, isPrivate, restrictAccess = [], hideAmazonConnect = false }) => {
      return (
        <Route key={path} path="/" element={<AppRoute isPrivate={isPrivate} restrictAccess={restrictAccess} />}>
          <Route
            path={path}
            element={
              <Box>
                {navBar ? (
                  <Box display={'flex'}>
                    <SideBar />
                    <ScrollProvider>
                      <Page />
                      <AmazonConnect isEnabled={!hideAmazonConnect} />
                    </ScrollProvider>
                  </Box>
                ) : (
                  <Page />
                )}
              </Box>
            }
          />
        </Route>
      );
    })}
  </Routes>
);

export const AppRoutes = () => {
  return (
    <BrowserRouter>
      <AuthenticatedSnowplow>
        <AmazonConnectProvider>
          <AppRoutesWithoutRouter />
        </AmazonConnectProvider>
      </AuthenticatedSnowplow>
    </BrowserRouter>
  );
};
